/**
 * Meal Plan Configuration
 * Centralized config for meal planning features
 */

// Meal taxonomy (shared enum - single source of truth)
export const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'] as const;
export type MealType = typeof MEAL_TYPES[number];

// Configuration constants
export const MEAL_PLAN_CONFIG = {
  WEEK_START: 0, // 0=Sunday, 1=Monday (locale-aware)
  MIN_SERVINGS: 1,
  MAX_SERVINGS: 12,
  UNDO_TIMEOUT_MS: 7000, // 7s to undo
  LOCALE: 'en-US', // TODO: get from user settings
} as const;

/**
 * Get locale-aware day labels
 * @param locale - BCP 47 language tag (e.g., 'en-US', 'es-ES')
 * @returns Array of day names starting from configured week start
 */
export function getDayLabels(locale: string = MEAL_PLAN_CONFIG.LOCALE): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  const referenceDate = new Date(2024, 0, MEAL_PLAN_CONFIG.WEEK_START); // Use 2024 as stable reference

  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(referenceDate);
    day.setDate(referenceDate.getDate() + i);
    return formatter.format(day);
  });
}

/**
 * Get locale-aware meal labels (i18n-ready)
 * @returns Object mapping meal types to display labels
 */
export function getMealLabels(): Record<MealType, string> {
  // TODO: Replace with i18n library when available
  return {
    breakfast: 'Breakfast',
    lunch: 'Lunch',
    dinner: 'Dinner',
    snack: 'Snack',
  };
}

/**
 * Get short day labels for compact UI
 * @param locale - BCP 47 language tag
 * @returns Array of abbreviated day names
 */
export function getShortDayLabels(locale: string = MEAL_PLAN_CONFIG.LOCALE): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const referenceDate = new Date(2024, 0, MEAL_PLAN_CONFIG.WEEK_START);

  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(referenceDate);
    day.setDate(referenceDate.getDate() + i);
    return formatter.format(day);
  });
}
